/**
 * Whether the editor holds nothing worth posting.
 *
 * There is no guard for this anywhere else. `convertEditedHtmlOrNotify` returns `null`
 * only when conversion *throws*, and an empty editor converts perfectly well; neither
 * `CreateCommentMutation` nor `CommentLogic.validate` checks either. So a blank comment
 * reaches the server if nothing stops it here.
 *
 * That was survivable while the add button was the only way to post. Enter makes it
 * reachable by accident — a stray keypress in a composer the user has not typed in yet.
 *
 * Emptiness is decided on the *rendered* content, not the markup: `<p><br></p>` is what an
 * untouched Plate editor serialises to, and it is as empty as `''`. Media counts as
 * content even though it contributes no text, so a comment that is only a pasted
 * screenshot is not empty.
 */
/**
 * Media serialises four ways in the task modal, and only three of them are elements.
 * Image, video and audio become `<img>` / `<video>` / `<audio>`; a **file** becomes
 * `<span class="slate-file-chip" data-file-ref=… data-name=…>` carrying the filename as
 * text. Stripping tags would leave that filename behind and read it as prose — which
 * happens to give the right answer, right up until the chip has no name to show, and then
 * a real attachment looks like an empty comment. `data-file-ref` is the attribute that is
 * always present, so match on it rather than on the text it happens to contain.
 */
const MEDIA_CONTENT = /<(img|video|audio|iframe|embed)\b|data-file-ref=/i;

export const isEditorEmpty = (html: string | undefined): boolean => {
	if (!html) return true;
	if (MEDIA_CONTENT.test(html)) return false;

	const text = html
		.replace(/<[^>]*>/g, '')
		.replace(/&nbsp;/gi, ' ')
		.trim();

	return text === '';
};
